import { DXF_MAPPING_ROLE, type DxfMappingRole } from '@frontend/api';
import type {
  ReadableStore,
  Selection,
  SelectionRef,
  ViewportSize,
  ViewportState,
} from '@frontend/viewer-core';

import { buildAccents } from './accentBuilder';
import type {
  AccentBatch,
  DrawingModel,
  DxfRenderTarget,
  MarqueePreview,
  MaterialFillRing,
  OverlayState,
  RoleIndex,
} from './types';

const NO_CUTTING_KEYS: ReadonlySet<string> = new Set();

type Dependencies = {
  viewportStore: ReadableStore<ViewportState>;
  sizeStore: ReadableStore<ViewportSize>;
  modelStore: ReadableStore<DrawingModel | null>;
  /** Subscribed, to know when to re-push the accents and the hidden-layer mask. */
  hiddenLayers: ReadableStore<ReadonlySet<number>>;
  getSelection: () => Selection;
  getHovered: () => SelectionRef | null;
  getHoveredLayer: () => number | null;
  getHoveredRole: () => DxfMappingRole | null;
  overlays: ReadableStore<OverlayState>;
  /** Fires when anything the accent overlay is drawn from changes, and not otherwise. */
  accentSource: ReadableStore<unknown>;
  preview: ReadableStore<MarqueePreview | null>;
  /** Fires on mapping edits and the mapping-visibility toggle, and not on hover. */
  roleOverlaySource: ReadableStore<unknown>;
  getRoleIndex: () => RoleIndex;
  /** Whether the drawing has any mapping to paint at all. */
  getHasMapping: () => boolean;
  /** Memoised per role — see `createRoleBatchBuilder`. */
  buildRoleBatches: (
    model: DrawingModel | null,
    roleIndex: RoleIndex,
    hiddenLayers: ReadonlySet<number>,
  ) => AccentBatch[];
  /** Memoised by reference — see `createMaterialFillBuilder`. */
  buildMaterialFill: (
    model: DrawingModel | null,
    cuttingKeys: ReadonlySet<string>,
    hiddenLayers: ReadonlySet<number>,
  ) => readonly MaterialFillRing[] | null;
};

/**
 * Wires session state into a renderer and keeps it in sync.
 *
 * Returns a function matching `DxfViewerSession.attachRenderer` exactly. Each
 * push is a cheap setter that no-ops when nothing changed and otherwise asks
 * the driver for a frame, so a burst of pointer events still draws once.
 */
export const createRendererBinding =
  ({
    viewportStore,
    sizeStore,
    modelStore,
    hiddenLayers,
    getSelection,
    getHovered,
    getHoveredLayer,
    getHoveredRole,
    overlays,
    accentSource,
    preview,
    roleOverlaySource,
    getRoleIndex,
    getHasMapping,
    buildRoleBatches,
    buildMaterialFill,
  }: Dependencies) =>
  (target: DxfRenderTarget): (() => void) => {
    const pushView = () =>
      target.setView(viewportStore.getState(), sizeStore.getState());
    const pushScene = () => {
      const model = modelStore.getState();
      target.setScene(model?.scene ?? null, model?.pmiEntityKeys);
    };
    const pushHidden = () => target.setHiddenLayers(hiddenLayers.getState());
    const pushAccents = () =>
      target.setAccents(
        buildAccents(
          modelStore.getState(),
          {
            selection: getSelection(),
            hovered: getHovered(),
            hoveredLayer: getHoveredLayer(),
            hoveredRole: getHoveredRole(),
            roleIndex: getRoleIndex(),
            hiddenLayers: hiddenLayers.getState(),
          },
          preview.getState(),
        ),
      );
    /** True when role colours are being painted, so they have the drawing to themselves. */
    const isRoleOverlayOn = () =>
      overlays.getState().mappingColoring && getHasMapping();

    const pushOverlayBatches = () =>
      target.setOverlayBatches(
        isRoleOverlayOn()
          ? buildRoleBatches(
              modelStore.getState(),
              getRoleIndex(),
              hiddenLayers.getState(),
            )
          : null,
      );

    const pushMaterialFill = () => {
      const model = modelStore.getState();
      target.setMaterialFill(
        model && isRoleOverlayOn()
          ? buildMaterialFill(
              model,
              getRoleIndex().get(DXF_MAPPING_ROLE.CUTTING) ?? NO_CUTTING_KEYS,
              hiddenLayers.getState(),
            )
          : null,
      );
    };

    const pushDisplay = () => {
      const current = overlays.getState();
      target.setPmiVisible(current.pmi);
      // Fills (hatches) go neutral underneath the role overlay, the same as
      // before: they carry no role batch of their own, so they still need
      // some color, and their own layer colours are arbitrary with respect
      // to manufacturing intent. Reuses the existing monochrome path rather
      // than adding a second one, and leaves the user's `layerColoring`
      // preference untouched — it simply does not apply while roles are what
      // is being shown.
      target.setLayerColoring(current.layerColoring && !isRoleOverlayOn());
      // Strokes, unlike fills, are hidden outright rather than recoloured —
      // every one of them already has a role batch painted over it once a
      // mapping exists, so the original line underneath a role's dash would
      // only show through the gaps for no reason.
      target.setStrokesVisible(!isRoleOverlayOn());
    };

    const unsubscribes = [
      viewportStore.subscribe(pushView),
      sizeStore.subscribe(pushView),
      modelStore.subscribe(pushScene),
      modelStore.subscribe(pushAccents),
      modelStore.subscribe(pushOverlayBatches),
      accentSource.subscribe(pushAccents),
      // The preview only republishes when the catch actually changes, so this
      // does not rebuild accent geometry as the box sweeps within one set.
      preview.subscribe(pushAccents),
      hiddenLayers.subscribe(pushHidden),
      overlays.subscribe(pushDisplay),
      // Deliberately its own trigger rather than `accentSource`: role geometry must
      // not be rebuilt because the pointer moved.
      roleOverlaySource.subscribe(pushOverlayBatches),
      // A mapping arriving or being toggled off changes whether the drawing beneath
      // is neutral, so display follows the same trigger.
      roleOverlaySource.subscribe(pushDisplay),
      // The fill depends on exactly the same inputs the role overlay does —
      // which loops are cutting, and whether roles are being shown at all.
      modelStore.subscribe(pushMaterialFill),
      roleOverlaySource.subscribe(pushMaterialFill),
    ];

    // The renderer arrives after the drawing has usually already loaded, so seed
    // it with everything rather than waiting for the next change.
    pushScene();
    pushHidden();
    pushAccents();
    pushOverlayBatches();
    pushMaterialFill();
    pushDisplay();
    pushView();

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
